'use client'

import { useState } from 'react'
import { faArrowLeft } from '@fortawesome/pro-light-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Button from '@ui/ButtonNew/Button'
import { Alert } from '@ui/kit'
import { registerUser, sendVerificationCode, VerificationError, verifyCode } from '@view/auth/api'
import { maskEmail } from '@view/auth/lib'
import { authFormStore } from '@view/auth/model'
import { observer } from 'mobx-react-lite'

import { useAppStore } from '@/entities/app-store'
import { getFlowFailureContext, type FailureStage } from '@/shared/analytics/events'
import { productAnalytics } from '@/shared/analytics/product-analytics'
import { CodeInput } from './code-input'
import { NoEmailHelp } from './no-email-help'
import { ResendCode } from './resend-code'

const CODE_LENGTH = 6

interface EmailVerificationProps {
  redirectPage: string
}

export const EmailVerification = observer(({ redirectPage }: EmailVerificationProps) => {
  const appStore = useAppStore()
  const [code, setCode] = useState('')
  const [errorMessage, setErrorMessage] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { email, password, consents, verificationEmail } = authFormStore

  const handleCodeChange = (next: string) => {
    setCode(next)
    setErrorMessage('')
  }

  const handleVerify = async () => {
    setIsSubmitting(true)
    setErrorMessage('')
    let failureStage: FailureStage = 'authorization'

    try {
      await verifyCode(verificationEmail, code)
      failureStage = 'request'
      await registerUser(email, password, consents)
      await appStore.refresh()
      window.location.href = redirectPage
    } catch (error: any) {
      productAnalytics.flowFailed(error, {
        ...getFlowFailureContext(error),
        failureStage,
        flowId: 'registration',
      })
      const fallback = 'Не удалось завершить регистрацию. Попробуйте ещё раз.'
      setErrorMessage(error instanceof VerificationError ? error.message : error?.response?.data?.errorText || fallback)
      setIsSubmitting(false)
    }
  }

  const handleResend = async () => {
    setErrorMessage('')
    try {
      const { cooldownSeconds } = await sendVerificationCode(verificationEmail)
      authFormStore.startResendCooldown(cooldownSeconds)
    } catch (error) {
      productAnalytics.flowFailed(error, {
        ...getFlowFailureContext(error),
        failureStage: 'request',
        flowId: 'registration',
      })
      setErrorMessage(
        error instanceof VerificationError ? error.message : 'Не удалось отправить письмо. Попробуйте ещё раз.'
      )
    }
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => authFormStore.backToCredentials()}
        className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400"
      >
        <FontAwesomeIcon icon={faArrowLeft} />
        Изменить e-mail
      </button>

      <h1 className="mt-4 text-center text-2xl font-bold text-gray-900 dark:text-white">Подтвердите почту</h1>
      <p className="mt-1 text-center text-sm text-gray-500 dark:text-gray-400">
        Мы отправили 6-значный код на
        <br />
        <span className="font-semibold text-gray-900 dark:text-white">{maskEmail(verificationEmail)}</span>
      </p>

      <div className="mt-6">
        <CodeInput value={code} onChange={handleCodeChange} disabled={isSubmitting} error={!!errorMessage} />
      </div>

      {errorMessage && (
        <Alert view="error" className="mt-4">
          <p className="text-sm">{errorMessage}</p>
        </Alert>
      )}

      <Button
        type="button"
        view="primary"
        viewSize="lg"
        fullWidth
        className="mt-6"
        loading={isSubmitting}
        disabled={code.length < CODE_LENGTH || isSubmitting}
        onClick={handleVerify}
      >
        Подтвердить
      </Button>

      <div className="mt-4">
        <ResendCode onResend={handleResend} />
      </div>

      <hr className="my-4 border-gray-200 dark:border-gray-600" />

      <NoEmailHelp onChangeEmail={() => authFormStore.backToCredentials()} />
    </div>
  )
})
